import { useState, useMemo } from 'react';
import { NeedsList } from '../../../types/needs';
import { mockNeedsLists } from '../../../data/mockNeedsData';

export const useNeedsFilters = (data: NeedsList[], searchQuery: string) => {
  const [appliedFilters, setAppliedFilters] = useState<any>({
    coordinator: { values: [] },
    group: { values: [] },
    status: { values: [] },
    isPastNeed: { values: ['false'] } // Default to current needs only
  });

  // Build toggle filter option maps
  const coordinatorOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(mockNeedsLists.map(n => n.coordinator))).map(coordinator => [coordinator, coordinator]))
  ), []);

  const groupOptionsMap = useMemo(() => (
    new Map(Array.from(new Set(mockNeedsLists.map(n => n.group))).map(group => [group, group]))
  ), []);

  const statusOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['Outstanding Needs', 'Outstanding Needs'],
      ['All Needs Fulfilled', 'All Needs Fulfilled'],
    ])
  ), []);

  const timeframeOptionsMap = useMemo(() => (
    new Map<string, string>([
      ['false', 'Current Needs'],
      ['true', 'Past Needs'],
    ])
  ), []);

  // Filter definitions for Filters.Bar
  const filterDefinitions: any = useMemo(() => {
    return [
      {
        field: 'coordinator',
        type: 'toggle',
        defaultLabel: 'Coordinator',
        options: coordinatorOptionsMap,
        filterState: appliedFilters.coordinator,
      },
      {
        field: 'group',
        type: 'toggle',
        defaultLabel: 'Group',
        options: groupOptionsMap,
        filterState: appliedFilters.group,
      },
      {
        field: 'status',
        type: 'toggle',
        defaultLabel: 'Status',
        options: statusOptionsMap,
        filterState: appliedFilters.status,
      },
      {
        field: 'isPastNeed',
        type: 'toggle',
        defaultLabel: 'Timeframe',
        options: timeframeOptionsMap,
        filterState: appliedFilters.isPastNeed,
      },
    ];
  }, [coordinatorOptionsMap, groupOptionsMap, statusOptionsMap, timeframeOptionsMap, appliedFilters]);

  // Apply filtering
  const filteredData = useMemo(() => {
    let rows = [...data];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter(r => 
        r.name.toLowerCase().includes(query) || 
        r.coordinator.toLowerCase().includes(query) ||
        r.group.toLowerCase().includes(query) ||
        r.nextItemNeeded.toLowerCase().includes(query)
      );
    }

    const selectedCoordinators: string[] = appliedFilters?.coordinator?.values || [];
    if (selectedCoordinators.length) {
      rows = rows.filter(r => selectedCoordinators.includes(r.coordinator));
    }

    const selectedGroups: string[] = appliedFilters?.group?.values || [];
    if (selectedGroups.length) {
      rows = rows.filter(r => selectedGroups.includes(r.group));
    }

    const selectedStatuses: string[] = appliedFilters?.status?.values || [];
    if (selectedStatuses.length) {
      rows = rows.filter(r => selectedStatuses.includes(r.status));
    }

    const timeframeSelections: string[] = appliedFilters?.isPastNeed?.values || [];
    if (timeframeSelections.length) {
      // If both selected, show all; if one selected, filter by that
      if (timeframeSelections.length === 1) {
        const isPast = timeframeSelections[0] === 'true';
        rows = rows.filter(r => r.isPastNeed === isPast);
      }
    }

    return rows;
  }, [data, appliedFilters, searchQuery]);

  return {
    appliedFilters,
    setAppliedFilters,
    filterDefinitions,
    filteredData,
  };
};
